import Container from "../components/Container/Container";
import {Card, Col, Row, DatePicker, Radio, Table} from "antd";
import useAuthHeader from "../hooks/useAuthHeader";
import {useContext, useEffect, useState} from "react";
import LoadingContext from "../contexts/LoadingContext";
import getStatistic from "../api/dashboard/getStatistic";
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import '../styles/Dashboard.css';

const { RangePicker } = DatePicker;

function Dashboard() {
    const accessToken = useAuthHeader();
    const {setLoading} = useContext(LoadingContext);
    const [dataTable, setDataTable] = useState([]);
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(6, 'day').startOf('day'),
        dayjs().endOf('day')
    ]);
    const [quickSelect, setQuickSelect] = useState('7days');

    const columns = [
        {
            title: 'Ngày',
            dataIndex: 'date',
            key: 'date',
            render: (text) => dayjs(text).format('DD/MM/YYYY')
        },
        {
            title: 'Số đơn nạp',
            dataIndex: 'depositCount',
            key: 'depositCount',
            align: 'right',
        },
        {
            title: 'Tiền nạp',
            dataIndex: 'depositAmount',
            key: 'depositAmount',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Số đơn rút',
            dataIndex: 'withdrawCount',
            key: 'withdrawCount',
            align: 'right',
        },
        {
            title: 'Tiền rút',
            dataIndex: 'withdrawAmount',
            key: 'withdrawAmount',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Phí nạp',
            dataIndex: 'depositFee',
            key: 'depositFee',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Phí rút',
            dataIndex: 'withdrawFee',
            key: 'withdrawFee',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Lợi nhuận',
            dataIndex: 'profit',
            key: 'profit',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        }
    ];

    const fetchStatistic = async (startDate, endDate) => {
        setLoading(true);
        const response = await getStatistic(accessToken, {
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD')
        });
        if (response.success) {
            // Transform data from object format to array format for table
            const tableData = Object.entries(response.data).map(([date, item]) => ({
                key: date,
                date: date,
                depositCount: item.depositCount || 0,
                depositAmount: parseFloat(item.depositAmount) || 0,
                withdrawCount: item.withdrawCount || 0,
                withdrawAmount: parseFloat(item.withdrawAmount) || 0,
                depositFee: item.depositFee || 0,
                withdrawFee: item.withdrawFee || 0,
                profit: item.profit || 0
            }));
            setDataTable(tableData);
        }
        setLoading(false);
    }

    useEffect(() => {
        if (dateRange) {
            fetchStatistic(dateRange[0], dateRange[1]).finally();
        }
    }, [dateRange]);

    const handleQuickSelectChange = (e) => {
        const value = e.target.value;
        setQuickSelect(value);
        
        let start, end;
        switch (value) {
            case '7days':
                start = dayjs().subtract(6, 'day').startOf('day');
                end = dayjs().endOf('day');
                break;
            case 'thisWeek':
                start = dayjs().startOf('week');
                end = dayjs().endOf('day');
                break;
            case 'lastWeek':
                start = dayjs().subtract(1, 'week').startOf('week');
                end = dayjs().subtract(1, 'week').endOf('week');
                break;
        }
        setDateRange([start, end]);
    };

    const handleRangePickerChange = (dates) => {
        setQuickSelect(null);
        setDateRange(dates);
    };

    return (
        <Container>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card
                        title={'Thống kê tổng quan'}
                        bordered={true}
                        className="dashboard-card"
                        extra={
                            <div className="dashboard-card-extra">
                                <Radio.Group value={quickSelect} onChange={handleQuickSelectChange}>
                                    <Radio.Button value="7days">7 ngày qua</Radio.Button>
                                    <Radio.Button value="thisWeek">Tuần này</Radio.Button>
                                    <Radio.Button value="lastWeek">Tuần trước</Radio.Button>
                                </Radio.Group>
                                <RangePicker 
                                    value={dateRange}
                                    onChange={handleRangePickerChange}
                                    format="DD/MM/YYYY"
                                />
                            </div>
                        }
                    >
                        <Table 
                            columns={columns} 
                            dataSource={dataTable}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            className="dashboard-table"
                            summary={(pageData) => {
                                const totals = pageData.reduce(
                                    (acc, curr) => ({
                                        depositCount: acc.depositCount + Number(curr.depositCount || 0),
                                        depositAmount: acc.depositAmount + Number(curr.depositAmount || 0),
                                        withdrawCount: acc.withdrawCount + Number(curr.withdrawCount || 0),
                                        withdrawAmount: acc.withdrawAmount + Number(curr.withdrawAmount || 0),
                                        depositFee: acc.depositFee + Number(curr.depositFee || 0),
                                        withdrawFee: acc.withdrawFee + Number(curr.withdrawFee || 0),
                                        profit: acc.profit + Number(curr.profit || 0)
                                    }),
                                    { depositCount: 0, depositAmount: 0, withdrawCount: 0, withdrawAmount: 0, depositFee: 0, withdrawFee: 0, profit: 0 }
                                );

                                return (
                                    <Table.Summary fixed>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}>Tổng cộng</Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right">
                                                {totals.depositCount}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={2} align="right">
                                                {totals.depositAmount.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={3} align="right">
                                                {totals.withdrawCount}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4} align="right">
                                                {totals.withdrawAmount.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} align="right">
                                                {totals.depositFee.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={6} align="right">
                                                {totals.withdrawFee.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={7} align="right">
                                                {totals.profit.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                );
                            }}
                        />
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default Dashboard;
